/**
 * TourEngine
 * Handles Driver.js integration, auto-fetching pending tours, and tour lifecycle.
 * Renders inside MainLayout — wraps the page content.
 */
import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import tourService from 'api/tour.service';
import { useTourContext } from 'contexts/TourContext';
import type { TourWithProgress, TourStep } from 'types/tour';

const isSuperAdmin = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}').role === 'super_admin';
  } catch {
    return false;
  }
};

const buildDriverSteps = (steps: TourStep[]) =>
  steps
    .slice()
    .sort((a, b) => a.step_order - b.step_order)
    .map(step => ({
      element: step.target_selector || undefined,
      popover: {
        title: step.title || undefined,
        description: step.content,
        side: step.position as 'top' | 'bottom' | 'left' | 'right',
        align: 'start' as const,
      },
    }));

const TourEngine = () => {
  const location = useLocation();
  const { setPendingTours, setAllTours, registerStartFn, setIsRunning } = useTourContext();
  const driverRef = useRef<ReturnType<typeof driver> | null>(null);
  const tourQueueRef = useRef<TourWithProgress[]>([]);
  const currentTourRef = useRef<TourWithProgress | null>(null);
  // Keep a local ref so startTourById can read all tours without state updates
  const allToursRef = useRef<TourWithProgress[]>([]);

  const runNextTour = useCallback(() => {
    const next = tourQueueRef.current.shift();
    if (!next) {
      setIsRunning(false);
      return;
    }

    currentTourRef.current = next;
    setIsRunning(true);

    const steps = buildDriverSteps(next.steps || []);
    if (steps.length === 0) {
      tourService.completeTour(next.id);
      runNextTour();
      return;
    }

    driverRef.current = driver({
      showProgress: true,
      animate: true,
      overlayColor: 'rgb(0 0 0 / 0.5)',
      progressText: '{{current}} / {{total}}',
      nextBtnText: 'Next →',
      prevBtnText: '← Back',
      doneBtnText: 'Done',
      steps,
      onDestroyed: () => {
        if (currentTourRef.current) {
          tourService.completeTour(currentTourRef.current.id);
          const completedId = currentTourRef.current.id;
          setPendingTours(allToursRef.current.filter(t => t.id !== completedId && !t.progress?.completed));
          currentTourRef.current = null;
        }
        runNextTour();
      },
    });

    driverRef.current.drive();
  }, [setIsRunning, setPendingTours]);

  const startTourById = useCallback(
    (tourId: string) => {
      const tour = allToursRef.current.find(t => t.id === tourId);
      if (tour) {
        if (driverRef.current?.isActive()) {
          driverRef.current.destroy();
        }
        tourQueueRef.current = [tour];
        runNextTour();
      }
    },
    [runNextTour]
  );

  // Register the start function so TourContext can trigger it
  useEffect(() => {
    registerStartFn(startTourById);
  }, [registerStartFn, startTourById]);

  // Fetch pending tours on route change
  useEffect(() => {
    if (isSuperAdmin()) return;
    if (!localStorage.getItem('auth_token')) return;

    const route = location.pathname;

    const fetchTours = async () => {
      try {
        const [pendingRes, allRes] = await Promise.all([
          tourService.getPendingTours(route),
          tourService.getAllTours(route),
        ]);

        const pending = pendingRes.data || [];
        const all = allRes.data || [];

        allToursRef.current = all;
        setPendingTours(pending);
        setAllTours(all);

        // Queue and start pending tours (if none running)
        if (pending.length > 0 && !driverRef.current?.isActive()) {
          tourQueueRef.current = [...pending];
          runNextTour();
        }
      } catch {
        // Silent fail — tour not critical
      }
    };

    // Small delay so page elements are mounted before tour starts
    const timer = setTimeout(fetchTours, 800);
    return () => clearTimeout(timer);
  }, [location.pathname, setPendingTours, setAllTours, runNextTour]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (driverRef.current?.isActive()) {
        driverRef.current.destroy();
      }
    };
  }, []);

  return null;
};

export default TourEngine;
